import React, { useEffect, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { fetchWorkOrderPaymetDetails } from "../../../services/api/workOrder";
import { acceptCounterOffer, fetchUserCounterOffer } from "../../../services/api/offers";
import {
  calculateNewEstimatedFullHalfDayTotal,
  calculateNewEstimatedHourlyTotal,
  expenseSideLabel,
} from "../../../utils/helpers";
import BaseButton from "../../../components/ui/BaseButton";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { CurrencySymbol } from "../../../utils/enum";

const ReviewCounterOfferModal = ({
  selectedOffer,
  reviewCounterOfferModalOpen,
  setReviewCounterOfferModalOpen,
  updateWorkOrderDetailPageUI,
  freelancerId,
  offerId,
  handleUpdateWorkOrderStatusButton,
  handleAssignWorkOrderWithRateCheck,
  handleAssignWorkOrderButton,
  isRateComparisonLoading,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [counterOfferData, setCounterOfferData] = useState({
    expenses: [],
    totalWorkOrderCost: "",
  });
  const {
    isLoading: isWorkOrderPaymentLoading,
    error,
    data: workOrderPaymentData,
  } = useQuery({
    queryFn: () => fetchWorkOrderPaymetDetails(id),
    enabled: !!id,
    queryKey: ["workOrderPaymentData"],
  });

  const companyOwnerId = selectedOffer?.createdBy?.ownedCompany
    ? selectedOffer?.createdBy?.id
    : null;

  const {
    isLoading: isUserCounterOfferDataLoading,
    error: isUserCounterOfferDataError,
    data: userCounterOfferData,
  } = useQuery({
    queryKey: ["userCounterOfferData", id, freelancerId, companyOwnerId],
    queryFn: () => fetchUserCounterOffer({ id, userId: freelancerId, companyOwnerId }),
    enabled: !!id && !!freelancerId,
  });

  const {
    isLoading: isAcceptCounterOfferLoading,
    error: isAcceptCounterOfferError,
    mutateAsync: acceptCounterOfferMutation,
  } = useMutation({
    mutationFn: acceptCounterOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Counter Offer Accepted Successfully",
          type: "success",
        }),
      );
    },
  });

  useEffect(() => {
    const workOrderExpenses = workOrderPaymentData?.data?.expenses || [];
    const userCounterExpenses = userCounterOfferData?.data?.counter?.expenses || [];
    if (userCounterOfferData?.data?.counter?.paymentType === "HOURLY") {
      setCounterOfferData({
        expenses: [...workOrderExpenses, ...userCounterExpenses],
        totalWorkOrderCost: calculateNewEstimatedHourlyTotal(
          [...workOrderExpenses, ...userCounterExpenses],
          userCounterOfferData?.data?.counter?.hourlyRate,
          userCounterOfferData?.data?.counter?.minimumHours,
        ),
      });
    }
    if (userCounterOfferData?.data?.counter?.paymentType === "HALF_DAY") {
      setCounterOfferData({
        expenses: [...workOrderExpenses, ...userCounterExpenses],
        totalWorkOrderCost: calculateNewEstimatedFullHalfDayTotal(
          [...workOrderExpenses, ...userCounterExpenses],
          userCounterOfferData?.data?.counter?.halfDayRate,
        ),
      });
    }
    if (userCounterOfferData?.data?.counter?.counter?.paymentType === "FULL_DAY") {
      setCounterOfferData({
        expenses: [...workOrderExpenses, ...userCounterExpenses],
        totalWorkOrderCost: calculateNewEstimatedFullHalfDayTotal(
          [...workOrderExpenses, ...userCounterExpenses],
          userCounterOfferData?.data?.counter?.fullDayRate,
        ),
      });
    }

    if (userCounterOfferData?.data?.counter?.paymentType === "FIXED") {
      setCounterOfferData({
        expenses: [...workOrderExpenses, ...userCounterExpenses],
        totalWorkOrderCost: calculateNewEstimatedFullHalfDayTotal(
          [...workOrderExpenses, ...userCounterExpenses],
          userCounterOfferData?.data?.counter?.fixedAmount,
        ),
      });
    }
  }, [userCounterOfferData, workOrderPaymentData]);

  const handleAcceptCounterOfferButton = async () => {
    try {
      await acceptCounterOfferMutation(offerId);
      updateWorkOrderDetailPageUI();
      setReviewCounterOfferModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const handleDeclineCounterOfferButton = async () => {
    await handleUpdateWorkOrderStatusButton(
      freelancerId,
      parseInt(id),
      "REJECTED",
      selectedOffer?.id,
    );
    updateWorkOrderDetailPageUI();
    setReviewCounterOfferModalOpen(false);
  };
  const getCurrentRate = () => {
    const rates = userCounterOfferData?.data?.current;
    if (!rates) return {};
    return {
      hourly: rates.hourlyRate || 0,
      halfDay: rates.halfDayRate || 0,
      fullDay: rates.fullDayRate || 0,
      fixedAmount: rates.fixedAmount || 0,
      currency: rates.currency || userCounterOfferData?.data?.counter?.engineerCurrency,
    };
  };

  const currentRate = getCurrentRate();
  const isFreelanceCompanyOffer = !!selectedOffer?.createdBy?.ownedCompany;
  const hasCurrentRates =
    userCounterOfferData?.data?.current?.hourlyRate != null ||
    userCounterOfferData?.data?.current?.halfDayRate != null ||
    userCounterOfferData?.data?.current?.fullDayRate != null ||
    userCounterOfferData?.data?.current?.fixedAmount != null;
  const currentTotal =
    userCounterOfferData?.data?.counter?.paymentType === "HOURLY" ||
    userCounterOfferData?.data?.counter?.paymentType === "FTH"
      ? calculateNewEstimatedHourlyTotal(
          userCounterOfferData?.data?.current?.expenses || [],
          currentRate.hourly,
          userCounterOfferData?.data?.current?.minimumHours > 0
            ? userCounterOfferData?.data?.current?.minimumHours
            : 1,
        )
      : calculateNewEstimatedFullHalfDayTotal(
          userCounterOfferData?.data?.current?.expenses || [],
          userCounterOfferData?.data?.current?.paymentType === "HALF_DAY"
            ? currentRate.halfDay
            : userCounterOfferData?.data?.current?.paymentType === "FULL_DAY"
              ? currentRate.fullDay
              : currentRate.fixedAmount,
        );

  const counterHourlyTotalWorkOrderCost = calculateNewEstimatedHourlyTotal(
    userCounterOfferData?.data?.counter?.expenses,
    userCounterOfferData?.data?.counter?.hourlyRate,
    userCounterOfferData?.data?.counter?.minimumHours > 0
      ? userCounterOfferData?.data?.counter?.minimumHours
      : 1,
  );
  return (
    <>
      {/*  <BaseLoader
        isLoading={isWorkOrderPaymentLoading || isUserCounterOfferDataLoading}
        zIndex={101}
      /> */}
      <BaseModal
        style={
          {
            // width: "900px",
          }
        }
        className="-top-60"
        open={reviewCounterOfferModalOpen}
        close={() => setReviewCounterOfferModalOpen(false)}
      >
        <div className="bg-[#F6F6F6] lg:w-[900px] sm:w-[600px] w-[350px]">
          <div className="container">
            <h6 className="text-dark border-bottom pb-1">
              {`${selectedOffer.isCounter ? " Counter Offer Review" : selectedOffer.status === "ACCEPTED" ? "Routed Offer Review" : "Requested Offer Review"}`}
              {!userCounterOfferData?.data?.isEngineerQualificationsVerified && (
                <span className="text-xs ml-2 text-red-500">
                  (The qualifications of this engineer have not been verified yet.)
                </span>
              )}
            </h6>
            {(!isFreelanceCompanyOffer || !selectedOffer.isCounter || hasCurrentRates) && (
              <BaseCard elevation={0} className="mt-5 pt-2 pb-4 px-5">
                <h6 className="text-dark border-bottom pb-1">Current Pay</h6>
                <div className="mt-2">
                  <h6 className="mt-2 text-dark">Labour</h6>
                  {(userCounterOfferData?.data?.current?.paymentType === "HOURLY" ||
                    userCounterOfferData?.data?.current?.paymentType === "FTH") && (
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Hourly Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[currentRate?.currency]}{" "}
                        {parseFloat(currentRate.hourly).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {userCounterOfferData?.data?.current?.paymentType === "HALF_DAY" && (
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Half Day Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[currentRate?.currency]}{" "}
                        {parseFloat(currentRate.halfDay).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {userCounterOfferData?.data?.current?.paymentType === "FULL_DAY" && (
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Full Day Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[currentRate?.currency]}{" "}
                        {parseFloat(currentRate.fullDay).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {(userCounterOfferData?.data?.current?.paymentType === "HALF_DAY" ||
                    userCounterOfferData?.data?.current?.paymentType === "FULL_DAY") && (
                    <div className="flex items-center mb-2">
                      <p className="mr-5">Additional Hourly Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[currentRate?.currency]} {parseFloat(currentRate?.hourly)}{" "}
                        /hr
                      </p>
                    </div>
                  )}
                  {userCounterOfferData?.data?.current?.paymentType === "FIXED" && (
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Fixed Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[currentRate?.currency]}{" "}
                        {parseFloat(currentRate?.fixedAmount)?.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {(userCounterOfferData?.data?.current?.paymentType === "HOURLY" ||
                    userCounterOfferData?.data?.current?.paymentType === "FTH") &&
                    userCounterOfferData?.data?.current?.minimumHours > 0 && (
                      <div className="flex items-center mb-2">
                        <p className="mr-5">Minimum Payable Hours</p>
                        <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                        <p className="ml-3 font-normal">
                          {userCounterOfferData?.data?.current?.minimumHours} hrs
                        </p>
                      </div>
                    )}
                  {(userCounterOfferData?.data?.current?.paymentType === "HOURLY" ||
                    userCounterOfferData?.data?.current?.paymentType === "FTH") &&
                    userCounterOfferData?.data?.current?.minimumHours > 0 && (
                      <div className="flex items-center mb-2">
                        <p className="mr-5">Additional Hourly rate</p>
                        <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                        <p className="ml-3 font-normal">
                          {CurrencySymbol[currentRate?.currency]}{" "}
                          {parseFloat(
                            userCounterOfferData?.data?.current?.additionalHourlyRate || 0,
                          )?.toFixed(2)}
                        </p>
                      </div>
                    )}
                </div>

                {userCounterOfferData?.data?.current?.expenses?.length > 0 && (
                  <>
                    <h6 className="text-dark">Expenses</h6>
                    {userCounterOfferData?.data?.current?.expenses?.map((expense, index) => {
                      return (
                        <div className="flex items-center mt-2 mb-3" key={index}>
                          <p className="mr-5">
                            {expense?.attachment?.url && (
                              <a
                                href={expense.attachment.url}
                                download={expense.attachment.fileName}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={expense.attachment.fileName}
                                className="hover:underline mr-2"
                              >
                                <i
                                  className="fa-solid fa-paperclip"
                                  style={{ color: "#0F40AE" }}
                                ></i>
                              </a>
                            )}
                            {expense?.expenseType ? expense.expenseType.charAt(0) + expense.expenseType.slice(1).toLowerCase() : ""} Expense{expense?.description?.trim() ? ` (${expense.description})` : ""}
                          </p>
                          <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                          <div className="flex items-center bg-light py-1 px-3 rounded-md ml-5">
                            {expense.isApproved ? (
                              <i className="fa-solid fa-check"></i>
                            ) : (
                              <i className="fa-solid fa-xmark"></i>
                            )}
                            <p className="ml-2 font-normal">
                              {!expense?.isEngineerExpense ? "Buyer" : "Provider"}:{" "}
                              {CurrencySymbol[currentRate?.currency]}{" "}
                              {expense?.amount?.toFixed(2) || 0}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                <div className="mt-6">
                  <div className="flex justify-between bg-primary px-4 py-1 rounded-md">
                    <h6 className="!text-white">Estimated Payable</h6>

                    <h6 className={`!text-white`}>
                      {userCounterOfferData?.data?.current?.currency || ""}{" "}
                      {currentTotal?.toFixed(2)}
                    </h6>
                  </div>
                </div>
              </BaseCard>
            )}

            {selectedOffer.isCounter && (
              <BaseCard elevation={0} className="mt-5 pt-2 pb-4 px-5">
                <h6 className="text-dark border-bottom pb-1">
                  {selectedOffer.isCounter ? "Counter Pay" : "Requested Pay"}
                </h6>
                {(userCounterOfferData?.data?.counter?.paymentType === "HOURLY" ||
                  userCounterOfferData?.data?.counter?.paymentType === "FTH") &&
                  (userCounterOfferData?.data?.counter?.hourlyRate ||
                    userCounterOfferData?.data?.counter?.minimumHours) && (
                    <div className="mt-2">
                      <h6 className="mt-2 text-dark">Labour</h6>
                      <div className="flex items-center mt-4 mb-2">
                        <p className="mr-5">Hourly Rate</p>
                        <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                        <p className="ml-3 font-normal">
                          {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                          {parseFloat(
                            userCounterOfferData?.data?.counter?.hourlyRate || 0,
                          )?.toFixed(2)}
                        </p>
                      </div>
                      {userCounterOfferData?.data?.counter?.minimumHours > 0 && (
                        <div className="flex items-center">
                          <p className="mr-5">Minimum Payable Hours</p>
                          <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                          <p className="ml-3 font-normal">
                            {userCounterOfferData?.data?.counter?.minimumHours || 0} hrs
                          </p>
                        </div>
                      )}
                      {userCounterOfferData?.data?.counter?.minimumHours > 0 && (
                        <div className="mt-2 flex items-center">
                          <p className="mr-5">Additional Hourly rate</p>
                          <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                          <p className="ml-3 font-normal">
                            {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                            {parseFloat(
                              userCounterOfferData?.data?.counter?.additionalHourlyRate || 0,
                            )?.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                {userCounterOfferData?.data?.counter?.paymentType === "HALF_DAY" && (
                  <div className="mt-2">
                    <h6 className="mt-2 text-dark">Labour</h6>
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Half Day Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                        {parseFloat(userCounterOfferData?.data?.counter?.halfDayRate || 0)?.toFixed(
                          2,
                        )}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <p className="mr-5">Additional Hourly Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                        {userCounterOfferData?.data?.counter?.hourlyRate || 0} /hr
                      </p>
                    </div>
                  </div>
                )}

                {userCounterOfferData?.data?.counter?.paymentType === "FULL_DAY" && (
                  <div className="mt-2">
                    <h6 className="mt-2 text-dark">Labour</h6>
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Full Day Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                        {parseFloat(userCounterOfferData?.data?.counter?.fullDayRate || 0)?.toFixed(
                          2,
                        )}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <p className="mr-5">Additional Hourly Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                        {userCounterOfferData?.data?.counter?.hourlyRate || 0} /hr
                      </p>
                    </div>
                  </div>
                )}

                {userCounterOfferData?.data?.counter?.paymentType === "FIXED" && (
                  <div className="mt-2">
                    <h6 className="mt-2 text-dark">Labour</h6>
                    <div className="flex items-center mt-4 mb-2">
                      <p className="mr-5">Fixed Rate</p>
                      <div className="border-t border-dotted border-gray-500  flex-grow"></div>

                      <p className="ml-3 font-normal">
                        {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                        {parseFloat(userCounterOfferData?.data?.counter?.fixedAmount || 0)?.toFixed(
                          2,
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {userCounterOfferData?.data?.counter?.expenses?.length > 0 && (
                  <>
                    <h6 className="text-dark mt-2">Expenses</h6>
                    {userCounterOfferData?.data?.counter?.expenses?.map((expense, index) => (
                      <div className="flex items-center mt-2 mb-3" key={index}>
                        <p className="mr-5">
                          {expense?.attachment?.url && (
                            <a
                              href={expense.attachment.url}
                              download={expense.attachment.fileName}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={expense.attachment.fileName}
                              className="hover:underline mr-2"
                            >
                              <i
                                className="fa-solid fa-paperclip"
                                style={{ color: "#0F40AE" }}
                              ></i>
                            </a>
                          )}
                          {expense?.expenseType ? expense.expenseType.charAt(0) + expense.expenseType.slice(1).toLowerCase() : ""} Expense{expense?.description?.trim() ? ` (${expense.description})` : ""}
                        </p>
                        <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                        <div className="flex items-center bg-light py-1 px-3 rounded-md ml-5">
                          {expense.isApproved ? (
                            <i className="fa-solid fa-check"></i>
                          ) : (
                            <i className="fa-solid fa-xmark"></i>
                          )}
                          <p className="ml-2 font-normal">
                            {expenseSideLabel(expense?.addedBy?.role?.role)}:{" "}
                            {CurrencySymbol[userCounterOfferData?.data?.counter?.engineerCurrency]}{" "}
                            {expense?.amount?.toFixed(2) || 0}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <div className="mt-6">
                  <div className="flex justify-between bg-secondary px-4 py-1 rounded-md">
                    <h6 className="!text-white">Estimated Payable</h6>

                    <h6 className={`!text-white`}>
                      {userCounterOfferData?.data?.counter?.engineerCurrency || ""}{" "}
                      {userCounterOfferData?.data?.counter?.paymentType === "HOURLY"
                        ? counterHourlyTotalWorkOrderCost.toFixed(2)
                        : parseFloat(
                            userCounterOfferData?.data?.counter?.totalWorkOrderCost,
                          )?.toFixed(2)}
                    </h6>
                  </div>
                </div>
              </BaseCard>
            )}
          </div>

          <div
            className="lg:flex grid grid-cols-2 gap-4 lg:justify-center  bg-white py-6 px-4"
            style={{
              boxShadow: "0px 0px 14px 5px rgba(0, 0, 0, 0.07)",
            }}
          >
            <div className="xl:block col-span-2">
              <BaseButton
                fullWidth={true}
                className="py-[4px] mr-6 bg-zinc-200 text-zinc-600 px-[30px]"
                color="primary"
                onClick={() => setReviewCounterOfferModalOpen(false)}
              >
                Close
              </BaseButton>
            </div>
            <div className="xl:block col-span-2">
              <BaseButton
                loaderSize="24px"
                fullWidth={true}
                className="py-[4px] mr-6 bg-zinc-200 text-zinc-600 px-[30px]"
                variant=""
                color="primary"
                onClick={handleDeclineCounterOfferButton}
              >
                Decline
              </BaseButton>
            </div>
            <div className="xl:block col-span-2">
              <BaseButton
                disabled={!userCounterOfferData?.data?.isEngineerQualificationsVerified}
                loaderSize="24px"
                isLoading={isRateComparisonLoading}
                fullWidth={true}
                className="py-[5px] min-w-36"
                color="primary"
                onClick={() => {
                  if (selectedOffer.isCounter) {
                    const mergedOffer = {
                      ...selectedOffer,
                      counterOfferData: userCounterOfferData?.data?.counter,
                    };

                    handleAssignWorkOrderWithRateCheck(mergedOffer, handleAcceptCounterOfferButton);
                  } else {
                    const mergedOffer = {
                      ...selectedOffer,
                      requested: userCounterOfferData?.data?.current,
                    };
                    handleAssignWorkOrderWithRateCheck(mergedOffer, () =>
                      handleAssignWorkOrderButton(freelancerId, offerId),
                    );
                  }
                }}
              >
                Accept & Assign
              </BaseButton>
            </div>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

export default ReviewCounterOfferModal;
